import React, { useEffect, useState } from 'react';
import {
  Modal,
  TextInput,
  Box,
  InputLabel,
  SelectMenu,
  InputDescription,
} from '@fullstory/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { MAX_DESCRIPTION_LENGTH } from '../../constants';
import { useDefaultSpaceSelectOption, useSpaceOptions } from '../../hooks';
import { REQUIRED_FIELD_STRING } from '../../utils';
import { ObjectType, Space } from '@fullstory/proto/fs/services/dx-objects';
import { DescriptionMarkdownPreview } from '@fullstory/junk-drawer-ui';

interface RenameSegmentModalProps extends React.ComponentProps<typeof Modal> {
  title: string;
  name: string;
  description: string;
  onSubmit: (newName: string, newDescription: string, space?: Space) => void;
  showSpaceSelect?: boolean;
}

// Modal for editing segment name and description
export const RenameSegmentModal: React.FC<RenameSegmentModalProps> = ({
  title,
  name,
  description,
  onSubmit,
  onCloseSignal,
  showSpaceSelect = false,
  ...rest
}) => {
  const intl = useIntl();
  const [newName, setNewName] = useState(name);
  const [newDescription, setNewDescription] = useState(description);
  const [hasDescriptionError, setHasDescriptionError] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(name == '');

  // copy props changes to local state
  useEffect(() => {
    setNewName(name);
  }, [name]);
  useEffect(() => {
    setNewDescription(description);
  }, [description]);

  useEffect(() => {
    setSaveDisabled(hasDescriptionError || newName == '');
  }, [newName, hasDescriptionError]);

  const {
    spaces,
    options: spaceOptions,
    selected: selectedSpace,
    setSelected: setSelectedSpace,
  } = useSpaceOptions(null, 'save');

  useDefaultSpaceSelectOption(
    rest.open ?? false,
    ObjectType.SEGMENT,
    '',
    selectedSpace,
    spaceOptions,
    setSelectedSpace,
    'save',
  );

  const handleSubmit = () => {
    const space = spaces.find(s => s.id === selectedSpace?.value);
    onSubmit(newName, newDescription, space);
  };

  return (
    <Modal data-component="RenameSegmentModal" {...rest} onCloseSignal={onCloseSignal}>
      <form
        className="fs_rename_segment_modal"
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Modal.Header onCloseSignal={onCloseSignal}>{title}</Modal.Header>

        <Modal.Body>
          <Box display="flex" flexDirection="column" gap={8}>
            <InputLabel htmlFor="name-input">
              <FormattedMessage defaultMessage="Name" id="HAlOn1" />
            </InputLabel>
            <TextInput
              id="name-input"
              testId="rename-segment-name-input"
              value={newName}
              onChange={e => {
                setNewName(e.target.value);
              }}
              placeholder={intl.formatMessage({ defaultMessage: 'Segment name', id: '7eLlIG' })}
            />
            <InputLabel htmlFor="description-input" optional>
              <FormattedMessage defaultMessage="Description" id="Q8Qw5B" />
            </InputLabel>
            <DescriptionMarkdownPreview
              id="description-input"
              testId="rename-segment-description-input"
              onChange={e => setNewDescription(e.target.value)}
              value={newDescription}
              rows={3}
              // Don't allow user to enter newlines in this textbox
              preventNewline
              maxLength={MAX_DESCRIPTION_LENGTH}
              setSaveDisabled={setHasDescriptionError}
            />
            {showSpaceSelect && spaceOptions.length > 0 && (
              <>
                <InputLabel htmlFor="segment-space-select">
                  <FormattedMessage defaultMessage="Space" id="0ah2ud" />
                </InputLabel>
                <Box>
                  <SelectMenu
                    id="segment-space-select"
                    filterable
                    onChange={opt => {
                      setSelectedSpace(opt);
                    }}
                    selected={selectedSpace}
                    options={spaceOptions ?? []}
                    variant="outline"
                    size="medium"
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Select a space',
                      id: 'R10+OI',
                    })}
                    fullWidth
                    menuHeight={385}
                  />
                </Box>
                {!selectedSpace && <InputDescription invalid description={REQUIRED_FIELD_STRING} />}
              </>
            )}
          </Box>
        </Modal.Body>
        <Modal.FooterActions
          primaryAction={{
            action: handleSubmit,
            text: intl.formatMessage({ defaultMessage: 'Save Segment', id: '1Tq0PS' }),
            disabled: saveDisabled,
          }}
          isBar
        />
      </form>
    </Modal>
  );
};
